import { useState } from "react";
import { Bell, ChevronDown, LogOut, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { API, useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import WorkspaceSwitcher from "@/components/dashboard/WorkspaceSwitcher";

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function TopBar({ title }) {
  const { user, logout, memberships, activeWorkspace } = useAuth();
  const navigate = useNavigate();
  const [notifications] = useState(0);
  const initials = (
    (user?.first_name?.[0] || user?.email?.[0] || "?") + (user?.last_name?.[0] || "")
  ).toUpperCase();

  const activeMembership = (memberships || []).find(
    (membership) => membership.workspace_owner === activeWorkspace
  );
  const ownWorkspace = !activeWorkspace || activeWorkspace === user?.id;
  const textName = (value) => typeof value === "string" ? value.trim() : "";
  const ownCompanyName = ownWorkspace
    ? textName(user?.company_profile?.legal_name) || textName(user?.company_name) ||
      textName(user?.company) || textName(user?.company_profile?.trading_name)
    : "";
  // Never label a colleague's workspace with the signed-in user's company.
  const workspaceLabel = textName(user?.workspace_context?.company_name) ||
    textName(activeMembership?.workspace_display_name) ||
    ownCompanyName || (ownWorkspace ? "My workspace" : "Workspace");
  const workspacePlan = user?.workspace_context?.subscription_plan || user?.subscription_plan;
  const workspaceOwner = activeWorkspace || user?.id;
  const hasCompanyLogo = user?.workspace_context?.has_company_logo ?? (ownWorkspace && user?.has_company_logo);
  const companyLogoSrc = hasCompanyLogo && workspaceOwner
    ? `${API}/account/logo?u=${encodeURIComponent(workspaceOwner)}`
    : null;
  const actorLabel = [textName(user?.first_name), textName(user?.last_name)]
    .filter(Boolean).join(" ") || textName(user?.email) || "Account";

  return (
    <header className="relative h-[64px] sm:h-[72px] bg-white border-b border-[#eee] flex items-center justify-between pl-14 lg:pl-6 pr-4 sm:pr-8 sticky top-0 z-20">
      <div className="min-w-0 flex-1 pr-3">
        {title ? (
          <h1 className="text-[16px] sm:text-[20px] font-semibold tracking-tight text-black truncate">{title}</h1>
        ) : (
          <h1 className="text-[16px] sm:text-[20px] font-semibold tracking-tight text-black truncate" data-testid="greeting">
            {greeting()}, {user?.first_name || "there"}
          </h1>
        )}
      </div>

      <div
        className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center justify-center gap-2.5 max-w-[30%] min-w-0"
        data-testid="active-company-brand"
      >
        {companyLogoSrc && (
          <img
            src={companyLogoSrc}
            alt=""
            className="w-8 h-8 rounded-md object-contain shrink-0"
            data-testid="active-company-logo"
          />
        )}
        <span
          className="text-[13px] font-semibold text-[#222] truncate notranslate"
          title={workspaceLabel}
          data-testid="active-company-name"
          translate="no"
        >
          {workspaceLabel}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <WorkspaceSwitcher />
        {workspacePlan && (
          <span
            className="text-[11.5px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full notranslate"
            style={{ background: "#EAF0FF", color: "#202B60" }}
            data-testid="plan-badge"
            translate="no"
          >
            {workspacePlan}
          </span>
        )}
        <button className="relative p-2 rounded-md hover:bg-[#F4F6FB]" aria-label="Notifications" data-testid="notifications">
          <Bell size={18} />
          {notifications > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: "#202B60" }} />
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#F4F6FB]" data-testid="profile-menu">
              <span
                className="w-8 h-8 rounded-full inline-flex items-center justify-center text-white text-[12px] font-semibold"
                style={{ background: "#202B60" }}
              >
                {initials}
              </span>
              <span className="hidden md:block max-w-[140px] truncate text-[12px] font-medium"
                title={actorLabel} data-testid="logged-in-user-name">{actorLabel}</span>
              <ChevronDown size={14} className="text-[#666]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-[13px] font-semibold">{user?.first_name} {user?.last_name}</div>
              <div className="text-[11px] text-[#666] font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
              <User size={14} className="mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid="logout"
            >
              <LogOut size={14} className="mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
